import React, { memo, useCallback, useEffect, useRef } from 'react';
import { get } from 'lodash/fp';
import PropTypes from 'prop-types';
import { TransitionGroup } from 'react-transition-group';

import AddIcon from '@mui/icons-material/Add';
import { Box, TextField } from '@mui/material';
import Collapse from '@mui/material/Collapse';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import theme from '../../../theme';
import { addHours, timeToMinutesFromMidnight } from '../../../utils';
import AlertComponent from '../../AlertComponent/AlertComponent';
import { WaeButton } from '../../Button/Button';
import TimePickerField from '../../TimePicker/TimePickerField';
import { errorHandler } from '../formUtils';
import shiftValidation from '../shiftValidations';

import ShiftSelectorCard from './ShiftSelectorCard';

const ShiftsTitleBox = styled(Box)(() => ({
  fontFamily: get(['shiftPicker', 'typography', 'boldFont'], theme),
  fontSize: get(['shiftPicker', 'typography', 'headerFontSize'], theme),
  color: get(['shiftPicker', 'typography', 'headerFontColor'], theme),
  margin: theme.spacing(0, 0, 2, 0),
}));

const StyledList = styled(List)(() => ({
  display: 'flex',
  borderRadius: '16px',
  padding: '5px',
}));

const FormShiftSelector = memo(
  ({
    shiftTitle,
    fieldErrorData,
    initialShifts,
    initialValue,
    input,
    label,
    mode,
    required,
    listViewGridSpacing,
    meta,
  }) => {
    const { onChange } = input;

    const [isScrollingToBottom, setIsScrollingToBottom] = React.useState(false);
    const [listData, setListData] = React.useState(initialShifts || []);
    const [errors, setErrors] = React.useState(null);
    const [isShiftSelected, setIsShiftSelected] = React.useState(false);
    const defaultShiftStartTime = new Date(new Date().setHours(0, 0, 0, 0));
    const defaultShiftEndTime = addHours(8, defaultShiftStartTime);
    const [shiftInputData, setShiftInputData] = React.useState({
      name: '',
      start: defaultShiftStartTime,
      end: defaultShiftEndTime,
    });

    const error = errorHandler(input, meta, fieldErrorData);
    const shiftInputDataName = get('name', shiftInputData);
    const shiftInputDataStart = get('start', shiftInputData);
    const shiftInputDataEnd = get('end', shiftInputData);

    const shiftSelectorEnd = useRef(false);
    // Styling
    let textFieldBackground;
    let textFieldBackgroundHover;
    let textFieldFocusedBorder;
    let textFieldErrorBorder;
    let textFieldPrimaryLabel;
    switch (mode) {
      case 'dark':
        // FIXME: Replace when dark mode colors are out

        textFieldBackground = get(['textField', 'darkBkColor'], theme);
        textFieldBackgroundHover = get(['palette', 'secondary', 'light'], theme);
        textFieldFocusedBorder = get(['textField', 'borderColor', 'focused'], theme);
        textFieldErrorBorder = get(['textField', 'borderColor', 'error'], theme);
        textFieldPrimaryLabel = get(['textField', 'inputLabel', 'primary', 'light'], theme);

        break;
      default:
        textFieldBackground = get(['textField', 'inputLabel', 'primary', 'light'], theme);
        textFieldBackgroundHover = get(['textField', 'background', 'light'], theme);
        textFieldFocusedBorder = get(['textField', 'borderColor', 'focused'], theme);
        textFieldErrorBorder = get(['textField', 'borderColor', 'error'], theme);
        textFieldPrimaryLabel = get(['textField', 'inputLabel', 'primary', 'focused'], theme);

        break;
    }

    const styleForTextField = {
      '& legend': { display: 'none' },
      '& fieldset': { top: 0 },
      '& .MuiOutlinedInput-root': {
        backgroundColor: textFieldBackground,
        borderRadius: '40px',
        fontFamily: 'Barlow',
        '&:hover': {
          backgroundColor: textFieldBackgroundHover,
        },
      },
      '& .MuiOutlinedInput-root.Mui-focused': {
        outline: `1px solid ${textFieldFocusedBorder}`,
        backgroundColor: textFieldBackground,
      },
      '& .MuiOutlinedInput-root.Mui-error': {
        outline: `1px solid ${textFieldErrorBorder}`,
      },
      '& .MuiInputLabel-root': {
        top: '-2px',
        color: textFieldPrimaryLabel,
      },
      '& .MuiInputLabel-root.Mui-focused': {
        top: '-8px',
        color: textFieldPrimaryLabel,
      },
      '& .MuiInputLabel-root.Mui-error': {
        color: textFieldPrimaryLabel,
      },
      '& .MuiOutlinedInput-input': {
        padding: theme.spacing(1, 7, 1, 2),
      },
      '& .MuiOutlinedInput-notchedOutline': {
        border: 0,
      },
      '& .MuiFormLabel-asterisk.Mui-error': {
        color: textFieldPrimaryLabel,
      },
      '& .MuiInputBase-root': {
        color: textFieldPrimaryLabel,
      },
      '& .MuiInputBase-root.Mui-focused': {
        fontWeight: 500,
      },
    };

    const descriptionStyle = {
      color: get(['shiftSelector', 'typography', 'additionalShiftDescriptionFontColor'], theme),
      fontSize: get(['shiftSelector', 'typography', 'additionalShiftDescriptionFontSize'], theme),
      fontFamily: get(
        ['shiftSelector', 'typography', 'additionalShiftDescriptionFontFamily'],
        theme
      ),
      lineHeight: get(
        ['shiftSelector', 'typography', 'additionalShiftDescriptionLineHeight'],
        theme
      ),
      margin: theme.spacing(0, 0, 2, 1),
    };

    // End of Styling

    const scrollToBottom = useCallback(() => {
      shiftSelectorEnd.current?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    useEffect(() => {
      if (isScrollingToBottom) {
        scrollToBottom();
        setIsScrollingToBottom(false);
      }
    }, [scrollToBottom, isScrollingToBottom]);

    const addShiftOnClick = () => {
      const foundErrors = shiftValidation(
        shiftInputDataName,
        shiftInputDataStart,
        listData,
        shiftInputDataEnd
      );
      setErrors(foundErrors);
      if (foundErrors) {
        return;
      }

      setListData([
        ...listData,
        {
          ...shiftInputData,
          start: timeToMinutesFromMidnight(shiftInputDataStart),
          end: timeToMinutesFromMidnight(shiftInputDataEnd),
        },
      ]);
      setShiftInputData({
        name: '',
        start: defaultShiftStartTime,
        end: defaultShiftEndTime,
      });
      setIsScrollingToBottom(true);
    };

    const nameShiftInputOnChange = (event) => {
      setShiftInputData({ ...shiftInputData, name: event.target.value });
      setErrors(null);
    };

    const startTimeShiftInputOnChange = (value) => {
      value &&
        setShiftInputData({
          ...shiftInputData,
          start: new Date(value.toString()),
        });
      setErrors(null);
    };
    const endTimeShiftInputOnChange = (value) => {
      value && setShiftInputData({ ...shiftInputData, end: new Date(value.toString()) });
      setErrors(null);
    };

    const ShiftInputComponent = (
      <Grid
        container
        sx={
          isShiftSelected
            ? { pointerEvents: 'none', opacity: '0.4', margin: theme.spacing(4, 0, 0, 0) }
            : { margin: theme.spacing(4, 0, 0, 0) }
        }
      >
        <ShiftsTitleBox>Additional Shifts</ShiftsTitleBox>
        <Grid item xs={12}>
          <TextField
            inputProps={{ 'aria-label': 'shiftDialogTextInput' }}
            error={(errors && errors.name) || null}
            fullWidth
            label="Shift Name"
            onChange={nameShiftInputOnChange}
            placeholder="Morning Shift"
            size="small"
            value={shiftInputDataName}
            sx={styleForTextField}
          />
          <Typography
            sx={{
              ...descriptionStyle,
              margin: theme.spacing(0, 0, 4, 2),
              width: '416px',
            }}
          >
            A quick description of the shift to identify it while selecting throughout the system.{' '}
          </Typography>
        </Grid>
        <Grid item xs={6} sx={{ margin: theme.spacing(0, 0, 0, 0) }}>
          <TimePickerField
            label="Start time"
            onChange={startTimeShiftInputOnChange}
            size="small"
            value={shiftInputDataStart}
            sx={{ width: '216px' }}
            error={
              (errors && errors.start) || Number.isNaN(Date.parse(shiftInputDataStart))
                ? 'Incorrect time format'
                : undefined
            }
          />
          <Typography sx={descriptionStyle}>When the shift begins.</Typography>
        </Grid>
        <Grid item xs={6}>
          <TimePickerField
            label="End time"
            onChange={endTimeShiftInputOnChange}
            size="small"
            value={shiftInputDataEnd}
            sx={{ width: '216px' }}
            error={
              (errors && errors.end) || Number.isNaN(Date.parse(shiftInputDataEnd))
                ? 'Incorrect time format'
                : undefined
            }
          />
          <Typography sx={descriptionStyle}>When the shift ends. </Typography>
        </Grid>

        <Grid item xs={12} sx={{ marginBottom: '10px' }}>
          <AlertComponent
            isOpen={!!errors}
            severity="error"
            message={(errors && Object.values(errors)[0]) || null}
          />
        </Grid>
        <Grid container spacing={1}>
          <Grid item>
            <WaeButton
              startIcon={<AddIcon />}
              onClick={() => {
                addShiftOnClick();
              }}
              text="Add Shift"
            />
          </Grid>
        </Grid>
      </Grid>
    );

    const shiftTitleCheckIfRequired = required ? `${shiftTitle}*` : shiftTitle;

    return (
      <Grid container spacing={listViewGridSpacing}>
        <Grid item xs>
          <ShiftsTitleBox>
            {shiftTitle && shiftTitleCheckIfRequired}
            <Grid item xs={12} required={required}>
              <FormHelperText
                sx={{
                  color: 'red',
                  margin: theme.spacing(0, 0, 0, 0.5),
                }}
              >
                {error}
              </FormHelperText>
            </Grid>
          </ShiftsTitleBox>

          <StyledList name={input.name} label={label} values={input.values || initialValue}>
            <Box
              sx={{
                width: '100%',
                maxHeight: '235px',
                overflow: 'auto',
                '&::-webkit-scrollbar': { display: 'none' },
              }}
            >
              <TransitionGroup>
                {(listData.length > 0 &&
                  listData.map((rowData) => (
                    <ShiftSelectorCard
                      shift={rowData}
                      onChange={onChange}
                      isAnotherOptionSelected={isShiftSelected}
                      setIsAnotherOptionSelected={setIsShiftSelected}
                      key={get('_id', rowData) || get('name', rowData)}
                    />
                  ))) || (
                  <Collapse>
                    <Box
                      sx={{
                        fontFamily: get(['shiftPicker', 'typography', 'assignedShifts'], theme),
                        color: get(
                          ['shiftPicker', 'palette', 'assignedShifts', 'emptyTextColor'],
                          theme
                        ),
                        fontSize: get(['shiftPicker', 'typography', 'defaultFontSize'], theme),
                        height: '72px',
                      }}
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                    >
                      Add a shift above to see it here.
                    </Box>
                  </Collapse>
                )}
                <Box ref={shiftSelectorEnd} />
              </TransitionGroup>
            </Box>
          </StyledList>
        </Grid>
        {ShiftInputComponent}
      </Grid>
    );
  }
);
FormShiftSelector.propTypes = {
  shiftTitle: PropTypes.string,
  fieldErrorData: PropTypes.string,
  initialShifts: PropTypes.arrayOf(PropTypes.shape({})),
  initialValue: PropTypes.arrayOf(PropTypes.shape({})),
  input: PropTypes.shape({
    name: PropTypes.string,
    onChange: PropTypes.func,
    values: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  label: PropTypes.string,
  mode: PropTypes.string,
  required: PropTypes.bool,
  listViewGridSpacing: PropTypes.number,
  meta: PropTypes.shape(),
};
export default FormShiftSelector;
